import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import useRequestItems from '../../../hooks/useRequestItems';

import { uploadPackingList } from '../apiService';

import { useMutation } from '@tanstack/react-query';

import { useFormData } from '../../../contexts/FormData';

import { useFormikContext } from 'formik';

/* ------------------------------------------------------------------ */
/* palette (matches the Shipment Details screen)                       */
/* ------------------------------------------------------------------ */
const PURPLE = '#7C3AED';
const PURPLE_SOFT = '#F3F0FF';
const PURPLE_BORDER = '#C9B8FA';
const GREEN = '#16A34A';
const GREEN_SOFT = '#E7F6ED';
const AMBER = '#C77700';
const AMBER_SOFT = '#FBF0DC';
const AMBER_BORDER = '#EBCF97';
const RED = '#EF4444';
const RED_SOFT = '#FDE8E8';
const BORDER = '#E7E7EC';
const BORDER_STRONG = '#D9D9E0';
const MUTED = '#9AA0A6';
const TEXT = '#2B2B36';

/* disabled-state greys, so the zone reads as inactive rather than broken */
const DISABLED_BG = '#FAFAFB';
const DISABLED_BORDER = '#DCDCE3';
const DISABLED_TEXT = '#A8A8B3';

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const ACCEPT = '.pdf,.csv,.xlsx,.xls,image/*';

/* Free-text hints sent alongside the file. Enough for a couple of sentences
   ("prices are in CAD", "skip the sample rows at the bottom"), not an essay.
   Mirror this limit on the backend. */
const MAX_INSTRUCTIONS = 300;

/* Editable fields shown on each wig card.
     mono     — render the value in a monospace face so codes line up with the raw line
     optional — only shown when at least one wig on this document has a value.
                Vendors who never list the field shouldn't see an empty column. */
const FIELDS = [
  { key: 'wig_id', label: 'Wig ID', mono: true },
  { key: 'color', label: 'Color' },
  { key: 'length', label: 'Length' },
  { key: 'length_unit', label: 'Unit' },
  { key: 'cap_size', label: 'Cap size' },
  { key: 'material', label: 'Wig Type' },
  { key: 'hairstyle', label: 'Hairstyle' },
  { key: 'coloring', label: 'Coloring', optional: true },
  { key: 'line', label: 'Collection', optional: true },
  { key: 'cost', label: 'Cost' },
  { key: 'price', label: 'Price' },
];

const uid = () => Math.random().toString(36).slice(2, 10);

const isBlank = (v) => v === null || v === undefined || v === '';

/* Which optional fields this document actually uses. Decided once per upload,
   not per render — otherwise clearing the last value would make the field
   disappear from every card while the user is still editing. */
const optionalKeysPresent = (list = []) =>
  FIELDS.filter((f) => f.optional && list.some((w) => !isBlank(w[f.key]))).map((f) => f.key);

/* Normalise one item from the extraction response into the shape stored in
   formValues.wigs. `_id` is a stable client key; `source_line` and `flags`
   drive the review UI and can be stripped before you persist. */
const toWig = (raw = {}) => ({
  _id: raw._id || uid(),
  wig_id: raw.wig_id ?? '',
  color: raw.color ?? '',
  length: raw.length ?? '',
  length_unit: raw.length_unit ?? '',
  cap_size: raw.cap_size ?? '',
  material: raw.material ?? '',
  hairstyle: raw.hairstyle ?? '',
  coloring: raw.coloring ?? '',
  line: raw.line ?? '',
  price: raw.price ?? '',
  cost: raw.cost ?? '',
  line_total: raw.line_total ?? '',
  description_raw: raw.description_raw ?? '',
  source_line: raw.source_line ?? '',
  unparsed_remainder: raw.unparsed_remainder ?? '',
  sku: raw.sku ?? '',
  flags: Array.isArray(raw.flags) ? raw.flags : [],
});

/* ------------------------------------------------------------------ */
/* styled bits                                                         */
/* ------------------------------------------------------------------ */
const Dropzone = styled(Box, {
  shouldForwardProp: (p) => p !== 'dragging' && p !== 'disabled',
})(({ dragging, disabled }) => ({
  border: `2px dashed ${disabled ? DISABLED_BORDER : dragging ? PURPLE : PURPLE_BORDER}`,
  borderRadius: 16,
  background: disabled ? DISABLED_BG : dragging ? '#EFE9FE' : PURPLE_SOFT,
  padding: '44px 24px',
  textAlign: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'border-color .15s, background .15s',
  outline: 'none',
  ...(disabled
    ? {}
    : {
        '&:hover': { borderColor: PURPLE, background: '#EFE9FE' },
        '&:focus-visible': { borderColor: PURPLE, boxShadow: `0 0 0 4px ${PURPLE_SOFT}` },
      }),
}));

const WigCard = styled(Box, {
  shouldForwardProp: (p) => p !== 'flagged',
})(({ flagged }) => ({
  border: `1px solid ${BORDER}`,
  borderLeft: `4px solid ${flagged ? AMBER : GREEN}`,
  borderRadius: 14,
  overflow: 'hidden',
}));

const CardHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  background: '#FAFAFC',
  borderBottom: `1px solid ${BORDER}`,
});

const LineNo = styled(Box)({
  fontFamily: MONO,
  fontWeight: 600,
  fontSize: 12,
  color: MUTED,
  background: '#EEEEF2',
  borderRadius: 6,
  padding: '4px 8px',
  flex: 'none',
});

const RawLine = styled(Typography)({
  fontFamily: MONO,
  fontSize: 13.5,
  fontWeight: 500,
  color: '#4A4A55',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  flex: 1,
  minWidth: 0,
});

const FieldGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '14px 16px',
  padding: '18px 16px',
});

const UnparsedBar = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '10px 16px',
  background: AMBER_SOFT,
  borderTop: `1px solid ${AMBER_BORDER}`,
  fontFamily: MONO,
  fontSize: 12.5,
  color: AMBER,
});

/* amber (not red) look for a field the extraction likely missed */
const warnFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: AMBER_BORDER },
  '& .MuiOutlinedInput-root': { backgroundColor: AMBER_SOFT },
  '& label': { color: AMBER },
  '& .MuiFormHelperText-root': { color: AMBER, marginLeft: 0 },
};

/* ------------------------------------------------------------------ */
/* component                                                           */
/* ------------------------------------------------------------------ */
export default function WigDocumentUpload() {
  const { formValues = {}, setFormValues } = useRequestItems();
  const { formValues: fv } = useFormData() || {};
  const { values, setFieldValue } = useFormikContext() || {};

  const wigs = formValues.wigs || [];

  /* The extractor is scoped to a vendor, so nothing can be uploaded until one
     is chosen. Gate here rather than failing on the server. */
  const hasVendor = Boolean(values?.vendor_id || fv?.vendor_name);

  const inputRef = useRef(null);
  const [phase, setPhase] = useState(() => (wigs.length ? 'review' : 'drop')); // 'drop' | 'processing' | 'review'
  const [fileName, setFileName] = useState('');
  const [docMeta, setDocMeta] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);

  /* Kept across "Different file" so the user doesn't retype it for a re-upload. */
  const [instructions, setInstructions] = useState('');

  /* Seeded from any wigs already in the form, so a remount mid-review keeps
     showing the same columns. */
  const [activeOptional, setActiveOptional] = useState(() => optionalKeysPresent(wigs));

  const visibleFields = useMemo(
    () => FIELDS.filter((f) => !f.optional || activeOptional.includes(f.key)),
    [activeOptional],
  );

  const stats = useMemo(() => {
    const review = wigs.filter((w) => (w.flags || []).length > 0).length;
    return { total: wigs.length, review, clean: wigs.length - review };
  }, [wigs]);

  /* write helper — always sets formValues.wigs */
  const setWigs = (updater) =>
    setFormValues((prev = {}) => ({
      ...prev,
      wigs: typeof updater === 'function' ? updater(prev.wigs || []) : updater,
    }));

  const { mutate } = useMutation({
    mutationFn: uploadPackingList,

    onSuccess: (result) => {
      const items = Array.isArray(result) ? result : result.items || [];
      const mapped = items.map(toWig);
      setWigs(mapped);
      setActiveOptional(optionalKeysPresent(mapped));
      setWarnings(Array.isArray(result) ? [] : result.warnings || []);
      setDocMeta(Array.isArray(result) ? null : result.document || null);
      setPhase('review');
    },
    onError: (e) => {
      setError(e.message || 'Something went wrong reading that file.');
      setPhase('drop');
    },
  });

  const handleFiles = async (fileList) => {
    if (!hasVendor) return;
    const file = fileList && fileList[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    setDocMeta(null);
    setWarnings([]);
    setPhase('processing');

    const additional_instructions = instructions.trim().slice(0, MAX_INSTRUCTIONS);

    mutate({
      file,
      vendor_id: values?.vendor_id,
      vendor_name: fv?.vendor_name,
      ...(additional_instructions ? { additional_instructions } : {}),
    });
  };

  // editing a field also clears that field's flag, since the user has now looked at it
  const updateField = (id, key, value) =>
    setWigs((list) =>
      list.map((w) =>
        w._id === id ? { ...w, [key]: value, flags: (w.flags || []).filter((f) => f !== key) } : w,
      ),
    );

  const dismissUnparsed = (id) =>
    setWigs((list) =>
      list.map((w) =>
        w._id === id
          ? { ...w, unparsed_remainder: '', flags: (w.flags || []).filter((f) => f !== '__unparsed') }
          : w,
      ),
    );

  const removeWig = (id) => setWigs((list) => list.filter((w) => w._id !== id));

  const reset = () => {
    setWigs([]);
    setActiveOptional([]);
    setFileName('');
    setDocMeta(null);
    setWarnings([]);
    setError('');
    setPhase('drop');
    if (inputRef.current) inputRef.current.value = '';
  };

  const openPicker = () => hasVendor && inputRef.current && inputRef.current.click();

  const remaining = MAX_INSTRUCTIONS - instructions.length;

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* ---------------- Dropzone ---------------- */}
      {phase === 'drop' && (
        <>
          <Dropzone
            role="button"
            tabIndex={hasVendor ? 0 : -1}
            aria-disabled={!hasVendor}
            dragging={dragging}
            disabled={!hasVendor}
            onClick={openPicker}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openPicker()}
            onDragOver={(e) => {
              e.preventDefault();
              if (hasVendor) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (hasVendor) handleFiles(e.dataTransfer.files);
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 2,
                borderRadius: '14px',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: hasVendor
                  ? '0 2px 8px rgba(124,58,237,.14)'
                  : '0 1px 4px rgba(0,0,0,.06)',
                color: hasVendor ? PURPLE : DISABLED_TEXT,
              }}
            >
              {hasVendor ? <AutoAwesomeOutlinedIcon /> : <StorefrontOutlinedIcon />}
            </Box>

            {hasVendor ? (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: 19, color: TEXT, mb: 0.5 }}>
                  Drop a packing list or invoice, or{' '}
                  <Box component="span" sx={{ color: PURPLE }}>
                    browse files
                  </Box>
                </Typography>
                <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 15 }}>
                  AI reads each line and fills in the wig details for you.
                </Typography>
                <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 13.5, mt: 0.75 }}>
                  It won't always get everything right — you'll review every line before saving.
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: 19, color: DISABLED_TEXT, mb: 0.5 }}>
                  Choose a vendor first
                </Typography>
                <Typography sx={{ color: DISABLED_TEXT, fontWeight: 600, fontSize: 15 }}>
                  Once a vendor is selected you can drop a packing list here.
                </Typography>
              </>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
              {['PDF', 'CSV', 'XLSX', 'JPG / PNG'].map((f) => (
                <Box
                  key={f}
                  sx={{
                    background: '#fff',
                    border: `1px solid ${hasVendor ? PURPLE_BORDER : DISABLED_BORDER}`,
                    borderRadius: 20,
                    px: 1.75,
                    py: 0.5,
                    fontWeight: 700,
                    fontSize: 13,
                    color: hasVendor ? PURPLE : DISABLED_TEXT,
                  }}
                >
                  {f}
                </Box>
              ))}
            </Box>
          </Dropzone>

          {/* ---------------- Additional instructions ---------------- */}
          <TextField
            name="additional_instructions"
            label="Additional instructions (optional)"
            placeholder="e.g. Prices are in CAD. Ignore the sample rows at the bottom."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value.slice(0, MAX_INSTRUCTIONS))}
            disabled={!hasVendor}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            inputProps={{ maxLength: MAX_INSTRUCTIONS }}
            helperText={
              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span>Anything the AI should know about this file.</span>
                <Box
                  component="span"
                  sx={{ flex: 'none', color: remaining <= 30 ? AMBER : 'inherit', fontWeight: 600 }}
                >
                  {instructions.length}/{MAX_INSTRUCTIONS}
                </Box>
              </Box>
            }
            FormHelperTextProps={{ sx: { mx: 0 } }}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PURPLE,
              },
              '& label.Mui-focused': { color: PURPLE },
            }}
          />

          {error && (
            <Typography sx={{ color: RED, fontWeight: 700, fontSize: 14, mt: 1.5, textAlign: 'center' }}>
              {error}
            </Typography>
          )}
        </>
      )}

      {/* ---------------- Processing ---------------- */}
      {phase === 'processing' && (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '16px', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '9px',
                background: RED_SOFT,
                color: RED,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InsertDriveFileOutlinedIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: TEXT }}>{fileName}</Typography>
            <Typography sx={{ ml: 'auto', color: MUTED, fontWeight: 600, fontSize: 14 }}>
              Reading lines &amp; parsing attributes…
            </Typography>
          </Box>
          <LinearProgress
            sx={{
              height: 8,
              borderRadius: 6,
              backgroundColor: '#EDEBF7',
              '& .MuiLinearProgress-bar': { backgroundColor: PURPLE },
            }}
          />
        </Box>
      )}

      {/* ---------------- Review ---------------- */}
      {phase === 'review' && (
        <>
          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: '14px',
              p: '16px 20px',
              mb: 2.75,
              background: '#FCFCFD',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '9px',
                    background: RED_SOFT,
                    color: RED,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: TEXT, lineHeight: 1.3 }}>
                    {fileName}
                  </Typography>
                  {docMeta && (docMeta.vendor_name || docMeta.document_number) && (
                    <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 13 }}>
                      {[docMeta.vendor_name, docMeta.document_number, docMeta.document_date]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: '#55555F' }}>
                {stats.total} lines&nbsp; ·&nbsp;{' '}
                <Box component="span" sx={{ color: GREEN }}>
                  {stats.clean} complete
                </Box>
                {stats.review > 0 && (
                  <>
                    &nbsp; ·&nbsp;{' '}
                    <Box component="span" sx={{ color: AMBER }}>
                      {stats.review} to review
                    </Box>
                  </>
                )}
              </Typography>

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                {docMeta?.layout_notes && (
                  <IconButton
                    size="small"
                    aria-label="How this document was read"
                    onClick={() => setShowNotes((s) => !s)}
                    sx={{ color: showNotes ? PURPLE : MUTED }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                <Button
                  onClick={reset}
                  startIcon={<CloudUploadOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#55555F',
                    border: `1px solid ${BORDER_STRONG}`,
                    borderRadius: '10px',
                    px: 2,
                    height: 48,
                    '&:hover': { background: '#F5F5F7', borderColor: BORDER_STRONG },
                  }}
                >
                  Different file
                </Button>
              </Box>
            </Box>

            <Collapse in={showNotes}>
              <Typography
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: `1px solid ${BORDER}`,
                  color: '#55555F',
                  fontSize: 13.5,
                  fontWeight: 500,
                }}
              >
                {docMeta?.layout_notes}
              </Typography>
            </Collapse>
          </Box>

          {/* document-level warnings */}
          {warnings.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${AMBER_BORDER}`,
                background: AMBER_SOFT,
                borderRadius: '12px',
                p: '12px 16px',
                mb: 2.5,
              }}
            >
              {warnings.map((w, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: i ? 1 : 0 }}>
                  <WarningAmberRoundedIcon sx={{ fontSize: 18, color: AMBER, mt: '1px', flex: 'none' }} />
                  <Typography sx={{ color: AMBER, fontWeight: 600, fontSize: 14 }}>{w}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {wigs.length === 0 ? (
            <Typography sx={{ color: MUTED, fontWeight: 600, textAlign: 'center', py: 4 }}>
              No wigs were found in that file. Try another, or switch to manual entry.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {wigs.map((w, i) => {
                const flags = w.flags || [];
                const flagged = flags.length > 0;
                return (
                  <WigCard key={w._id} flagged={flagged}>
                    <CardHead>
                      <LineNo>{String(i + 1).padStart(2, '0')}</LineNo>
                      <RawLine component="code" title={w.source_line}>
                        {w.source_line || '—'}
                      </RawLine>
                      {flagged ? (
                        <Chip
                          size="small"
                          icon={<WarningAmberRoundedIcon />}
                          label="Review"
                          sx={{
                            fontWeight: 800,
                            fontSize: 11,
                            letterSpacing: 0.4,
                            bgcolor: AMBER_SOFT,
                            color: AMBER,
                            '& .MuiChip-icon': { color: AMBER },
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon />}
                          label="Complete"
                          sx={{
                            fontWeight: 800,
                            fontSize: 11,
                            letterSpacing: 0.4,
                            bgcolor: GREEN_SOFT,
                            color: GREEN,
                            '& .MuiChip-icon': { color: GREEN },
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        aria-label="Remove wig"
                        onClick={() => removeWig(w._id)}
                        sx={{ color: RED }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </CardHead>

                    <FieldGrid>
                      {visibleFields.map((f) => {
                        const fieldFlagged = flags.includes(f.key);
                        return (
                          <TextField
                            key={f.key}
                            label={f.label}
                            value={w[f.key] ?? ''}
                            onChange={(e) => updateField(w._id, f.key, e.target.value)}
                            size="small"
                            fullWidth
                            helperText={fieldFlagged ? 'Check this' : undefined}
                            sx={fieldFlagged ? warnFieldSx : undefined}
                            InputProps={f.mono ? { sx: { fontFamily: MONO, fontSize: 14 } } : undefined}
                          />
                        );
                      })}
                    </FieldGrid>

                    {w.unparsed_remainder && (
                      <UnparsedBar>
                        <WarningAmberRoundedIcon sx={{ fontSize: 16, mt: '1px', flex: 'none' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>Unassigned text: {w.unparsed_remainder}</Box>
                        <Button
                          size="small"
                          onClick={() => dismissUnparsed(w._id)}
                          sx={{ textTransform: 'none', fontWeight: 700, color: AMBER, minWidth: 0, p: '0 6px' }}
                        >
                          Dismiss
                        </Button>
                      </UnparsedBar>
                    )}
                  </WigCard>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}